/* eslint-disable */
import store from "@/store/index.js";
import { checkMenuAccess, checkSubMenuAccess } from "@/services/accessControl";
import { getAdminProfiles } from "@/helpers/company-access";

const publicRouteLists = [
  "login",
  "reset-password",
  "download-attendance-report",
  "download-attendance-all-courses-report"
];

const isRouteAccessible = (to, user, profile) => {
  // Rule means menu and sub menu of the route
  const getRouteMetaAccessRule = () => {
    const route = to.matched.findLast(route => {
      return route.meta && route.meta.access !== undefined;
    });
    return route ? route.meta.access : undefined;
  };

  const getCurrentUser = () => {
    const companyId = Number(profile.company.id);
    const roles = (user.roles || [])
      .filter(role => role.companyId == companyId)
      .map(role => role.roleName);

    return {
      roles,
      comType: profile.company.companyType,
      comId: companyId,
      mId: user.id
    };
  };

  const ensureArray = (item) => !Array.isArray(item) ? [item] : item;

  const checkRouteAccessFromRule = (rule, currentUser) => {
    // Check main menu access
    if (!checkMenuAccess({ menu: rule.menu }, currentUser)) return false;
    if (!rule.subMenu) return true;

    // Check sub menu access
    const subMenus = ensureArray(rule.subMenu);
    return subMenus.some(subMenu => {
      return checkSubMenuAccess({ menu: subMenu }, currentUser);
    });
  };

  const rules = getRouteMetaAccessRule();
  if (rules === undefined) return false; // No meta.access rule = deny
  if (rules === null) return true; // null = open routes (dashboard, error, un-authorized)

  const currentUser = getCurrentUser();
  const ruleArray = ensureArray(rules);
  return ruleArray.some(rule => checkRouteAccessFromRule(rule, currentUser));
};

/**
 * Route access guard
 * 1. Check if the user has access to company profile (i.e., :company)
 * 2. Check if the user has access to the route (i.e., menu and sub menu)
 */
export const routeAccessGuard = (to, _from, next) => {
  const getStoredUser = () => {
    const user = localStorage.getItem("user");
    if (!user) return null;

    try {
      return JSON.parse(user); // Prevent modifier user local storage
    } catch (e) {
      return null;
    }
  };

  const findProfileByCompanyUrl = (profiles, companyUrl) => {
    return profiles.find(profile => profile.company.url === companyUrl);
  };

  const getFallbackProfile = profiles => {
    const currentComId = localStorage.getItem("ComId");
    return (
      profiles.find(profile => profile.company.id == currentComId) ||
      profiles[0]
    );
  };

  // Recently accessed company URL
  const setActiveCompany = company => {
    localStorage.setItem("ComId", company.id);
    store.dispatch("Company/setCompanyInfo", company);
  };

  const routeName = to.name;
  if (publicRouteLists.includes(routeName)) return next(); // Skip public routes

  const companyUrl = to.params.company;
  if (!companyUrl) return next();

  // 1. Check if the user has access to company profile
  const user = getStoredUser();
  if (!localStorage.getItem("token") || !user) {
    return next({ name: "login" });
  }

  const profiles = getAdminProfiles(user);
  if (!profiles.length) return next({ name: "login" });

  const profile = findProfileByCompanyUrl(profiles, companyUrl);
  if (!profile) {
    const fallbackProfile = getFallbackProfile(profiles);
    setActiveCompany(fallbackProfile.company);

    // Return unauthorized if no profile is found
    return next({
      name: "un-authorized",
      params: { company: fallbackProfile.company.url }
    });
  }

  setActiveCompany(profile.company);

  // 2. Check if the user has access to the route
  if (!isRouteAccessible(to, user, profile)) {
    return next({ name: "un-authorized", params: { company: companyUrl } });
  }

  return next();
};
